'use client';

import { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useConsent } from '@/hooks/use-consent';
import { analytics } from '@/lib/analytics/registry';
import { getAnalyticsConfig } from '@/lib/analytics/config';
import { InternalAdapter } from '@/lib/analytics/adapters/internal';
import { GA4Adapter } from '@/lib/analytics/adapters/ga4';
import { UmamiAdapter } from '@/lib/analytics/adapters/umami';
import { createGoogleAdsAdapter } from '@/lib/analytics/adapters/google-ads';

type GA4ContextValue = {
  trackEvent: (name: string, params?: Record<string, unknown>) => void;
};

const GA4Context = createContext<GA4ContextValue>({ trackEvent: () => {} });

export function useGA4() {
  return useContext(GA4Context);
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { consent } = useConsent();
  const pathname = usePathname();
  const registeredRef = useRef(false);
  const ga4AdapterRef = useRef<GA4Adapter | null>(null);
  const gadsAdapterRef = useRef<ReturnType<typeof createGoogleAdsAdapter>>(null);

  // Register adapters and init vitals/error handlers once
  useEffect(() => {
    if (registeredRef.current) return;
    registeredRef.current = true;

    const config = getAnalyticsConfig();

    analytics.init();
    analytics.register(new InternalAdapter());

    if (config.gaId) {
      const ga4 = new GA4Adapter(config.gaId);
      ga4AdapterRef.current = ga4;
      analytics.register(ga4);
    }

    const gads = createGoogleAdsAdapter();
    if (gads) {
      gadsAdapterRef.current = gads;
      analytics.register(gads);
    }

    if (config.umamiUrl && config.umamiWebsiteId) {
      const umami = new UmamiAdapter(config.umamiUrl, config.umamiWebsiteId);
      umami.load();
      analytics.register(umami);
    }

    import('@/lib/tracking').then(({ reportWebVitals, installErrorHandlers }) => {
      reportWebVitals();
      installErrorHandlers();
    });

    // Install rb_anon_id cookie + rb_session_id sessionStorage and patch
    // global fetch() to forward them as headers on same-origin calls.
    // sendBeacon callsites read the same values via getOrCreateAnonId() / getOrCreateSessionId().
    import('@/lib/tracking/identity').then(({ getOrCreateAnonId, getOrCreateSessionId, installFetchIdentityHeaders }) => {
      getOrCreateAnonId();
      getOrCreateSessionId();
      installFetchIdentityHeaders();
    });
  }, []);

  // Load consent-gated adapters (GA4 + Google Ads) when consent is accepted
  useEffect(() => {
    if (consent !== 'accepted') return;
    ga4AdapterRef.current?.load();
    gadsAdapterRef.current?.load();
  }, [consent]);

  // Track page views on route change.
  //
  // Historically we called BOTH analytics.page() AND analytics.track({name:'page_view'})
  // here, which dispatched the same logical event twice through InternalAdapter
  // → tracker.trackPageView() → /api/track → user_interactions. With journey
  // events now persisted, that became 2x DB rows per page view. Keep only the
  // explicit track() call (it carries properties, GA4 still gets a page_view
  // because page_view is dispatched as a normal event by the registry).
  useEffect(() => {
    if (!pathname) return;
    const level = consent === 'accepted' ? 'analytics' as const : 'essential' as const;
    analytics.track(
      { name: 'page_view', category: 'journey', properties: { path: pathname } },
      level,
    );
  }, [pathname, consent]);

  // Legacy GA4 context for any existing consumers
  const trackEvent = useCallback(
    (name: string, params?: Record<string, unknown>) => {
      const config = getAnalyticsConfig();
      if (consent !== 'accepted' || !config.gaId || typeof window === 'undefined' || !window.gtag) return;
      window.gtag('event', name, params);
    },
    [consent],
  );

  return <GA4Context.Provider value={{ trackEvent }}>{children}</GA4Context.Provider>;
}
